import paper from 'paper';
import { toGlobalID, uuid } from 'src/shared/helpers';
import { Outline } from 'src/shared/utilities/outline/outliner_worker';

export const DEFAULT_AREA_SIZE = [14.8, 21];

export interface DeletedAreaIdentifier {
	areaID: number;
	guid: string;
}

/**
 * Model area interface
 * @param areaID The area ID
 * @param guid The area guid
 * @param name The area name
 * @param pathSVG The area SVG path
 * @param path the area paper JSON path
 * @param boundsX the area bounds X
 * @param boundsY the area bounds Y
 * @param boundsWidth the area bounds width
 * @param boundsHeight the area bounds height
 * @param clipOut if area must be clipped in output files
 */
export interface ModelArea {
    areaID: number | null;
    guid: string;
    name: string;
    pathSVG: string;
    path: string;
    clipOut: boolean;
    boundsX: number;
    boundsY: number;
    boundsWidth: number;
    boundsHeight: number;
    layers: ModelAreaLayer[];
    templateFile: ModelAreaTemplateFile | null;
}

/**
 * Model area layer interface
 * @param id Relay global id as uuid
 * @param name Internal layer name
 * @param svg SVG structure of layer
 * @param json JSON structure of layer
 * @param displayOrder Order position
 * @param isVisibleInCanvas Should the layer visibile in canvas?
 * @param isVisibleInCart Should the layer visibile in cart preview?
 * @param isVisibleInOutput Should the layer visibile in output files?
 * @param showLabel Should the label visible in canvas?
 * @param isClipMask Use this layer as printing area clip
 * @param displayName Name to show in canvas
 * @param description Description to show in canvas
 */
export interface ModelAreaLayer {
    id: string;
    name: string;
    json: string;
    svg: string;
    displayOrder: number;
    isVisibleInCanvas: boolean;
    isVisibleInCart: boolean;
    isVisibleInOutput: boolean;
    showLabel: boolean;
    isClipMask: boolean;
    isOutputClipped: boolean;
    displayName: string;
    description: string;
}
/**
 * Model area template file interface
 * @param fileID Client generated file uuid used in chunked task
 * @param documentWidth Document width in centimeters
 * @param documentHeight Document height in centimeters
 */
export interface ModelAreaTemplateFile {
    fileID: string | null;
    fileObjectID: number | null;
    documentWidth: number;
    documentHeight: number;
}

// Sample area when adding a new side
export const createDefaultModelArea = (widthCM: number, heightCM: number, ppcm: number): ModelArea => {
    return {
        ...createAreaFromSizeInCM(widthCM, heightCM, ppcm)
    };
};

export const createAreaFromSizeInCM = (widthCM: number, heightCM: number, ppcm: number, reuseId?: DeletedAreaIdentifier): ModelArea => {
	return createAreaFromSizePX(widthCM * ppcm, heightCM * ppcm, reuseId);
};

export const createAreaFromSizePX = (widthPx: number, heightPx: number, reuseId?: DeletedAreaIdentifier): ModelArea => {
	const guid = reuseId?.guid ?? uuid();

    const paperScope = new paper.PaperScope();
    paperScope.setup(new paper.Size(widthPx, heightPx));

    const path = new paper.Path.Rectangle(new paper.Point(-widthPx / 2, -heightPx / 2), new paper.Size(widthPx, heightPx));

    path.data = {
        guid,
        isArea: true
    };

	return {
		areaID: reuseId?.areaID ?? null,
		name: '',
		clipOut: true,
		guid,
		pathSVG: path.exportSVG({ asString: true }) as string,
		path: path.exportJSON(),
		boundsX: path.bounds.x,
		boundsY: path.bounds.y,
		boundsWidth: path.bounds.width,
		boundsHeight: path.bounds.height,
		layers: [],
		templateFile: null
	};
};

export const createAreaFromModelAreaLayer = (
	layer: { name: string; json: string },
	ppcm: number,
	width: number,
	height: number,
	reuseId?: DeletedAreaIdentifier
): ModelArea => {
	const guid = reuseId?.guid ?? uuid();
	const widthPx = width * ppcm;
	const heightPx = height * ppcm;

    const paperScope = new paper.PaperScope();
    paperScope.setup(new paper.Size(widthPx, heightPx));

    // Import and scale the SVG complete to the document size
    const item = paperScope.project.importJSON(layer.json);
    item.scale(widthPx / item.bounds.width, heightPx / item.bounds.height);
    item.position = new paperScope.Point(0, 0);

    // Find the compound path
    const childrens = getAllChildrens(item);
    const paths = childrens.filter((x) => x instanceof paperScope.CompoundPath && x.strokeColor);

    const path = new paperScope.CompoundPath({
        children: paths.flatMap((x) => x.children)
    });

    if (!path) throw new Error('Compound path not found in layer JSON.');

	return {
		areaID: reuseId?.areaID ?? null,
		guid,
		name: '',
		clipOut: true,
		pathSVG: path.exportSVG({ asString: true }) as string,
		path: path.exportJSON(),
		boundsX: path.bounds.x,
		boundsY: path.bounds.y,
		boundsWidth: path.bounds.width,
		boundsHeight: path.bounds.height,
		layers: [],
		templateFile: null
	};
};

export const createAreaLayerFromTemplateLayer = (
    layer: { name: string; svg: string },
    area: ModelArea,
    width: number,
    height: number,
    ppcm: number
): ModelAreaLayer => {
    const id = uuid();
    const paperScope = new paper.PaperScope();
    paperScope.setup(new paper.Size(1024, 1024));

    const path = paperScope.project.importSVG(layer.svg, {
        applyMatrix: true,
        expandShapes: true
    });

    path.position = new paperScope.Point(0, 0);

    const scale = Math.min(path.bounds.width / (width * ppcm), path.bounds.height / (height * ppcm));

    path.scale(1 / scale);

    // Add default data values (IMPORTANT FOR OUTPUT FILES WHERE THE AREA IS NOT MOVED AFTER IMPORTR)
    path.data = {
        layerId: id,
        areaGuid: area.guid,
        offsetFromDocumentCenter: [0, 0]
    };

    return {
        id: toGlobalID(id, 'AreaLayer'),
        name: layer.name,
        displayName: layer.name,
        description: '',
        displayOrder: 0,
        isClipMask: false,
        isOutputClipped: false,
        isVisibleInCanvas: true,
        isVisibleInCart: true,
        isVisibleInOutput: true,
        showLabel: true,
        svg: path.exportSVG({ asString: true }) as string,
        json: path.exportJSON()
    };
};

export const getLayerColor = (layer: { json: string }): string => {
    const paperScope = new paper.PaperScope();
    paperScope.setup(new paper.Size(1024, 1024));

    const item = paperScope.project.importJSON(layer.json);
    const path = getAllChildrens(item).find((x) => x instanceof paperScope.CompoundPath && x.strokeColor);

    return path?.strokeColor?.toCSS(true) ?? '#000000';
};

export const createAreaFromOutlinesResult = (
	result: { outlines: Outline[]; width: number; height: number },
	targetWidth: number,
	targetHeight: number,
	reuseId?: DeletedAreaIdentifier
): ModelArea => {
	const guid = reuseId?.guid ?? uuid();
	const paperScope = new paper.PaperScope();
	paperScope.setup(new paper.Size(result.width, result.height));

    const paths: paper.Path.Rectangle[] = [];

    // Add the border so we can use all the bounding box
    paths.push(new paper.Path.Rectangle(new paper.Point(0, 0), new paper.Size(result.width, result.height)));

    result.outlines.forEach((outline) => {
        const points: paper.Point[] = [];

        outline.points.forEach((point) => {
            points.push(new paper.Point(point[0], point[1]));
        });

        const path = new paper.Path(points);

        path.closed = true;
        //path.simplify(1);

        paths.push(path);
    });

    // Create final compund path
    const compound = new paper.CompoundPath('');
    compound.addChildren(paths);
    compound.strokeColor = new paper.Color('black');
    compound.strokeWidth = 0.5;
    compound.dashOffset = 1;
    compound.dashArray = [1.5, 1.5];
    compound.position.x = 0;
    compound.position.y = 0;
    compound.data = {
        guid,
        isArea: true
    };

    let ratio: number | null = null;
    let width = result.width;
    let height = result.height;

    if (width != targetWidth) {
        ratio = targetWidth / width;
        compound.scale(ratio);
        height = height * ratio;
    }

    if (height > targetHeight) {
        ratio = targetHeight / height;
        compound.scale(ratio);
    }

    // Remove the rectangle for the bounding box
    compound.removeChildren(0, 1);
    compound.reorient(false, true);

	return {
		areaID: reuseId?.areaID ?? null,
		guid,
		name: '',
		clipOut: true,
		pathSVG: compound.exportSVG({ asString: true }) as string,
		path: compound.exportJSON(),
		boundsX: compound.bounds.x,
		boundsY: compound.bounds.y,
		boundsWidth: compound.bounds.width,
		boundsHeight: compound.bounds.height,
		layers: [],
		templateFile: null
	};
};

export const createAreaFromPaperItem = (item: paper.Item, reuseId?: DeletedAreaIdentifier): ModelArea => {
	const guid = reuseId?.guid ?? uuid();

	return {
		areaID: reuseId?.areaID ?? null,
		guid,
		name: '',
		clipOut: true,
		pathSVG: item.exportSVG({ asString: true }) as string,
		path: item.exportJSON(),
		boundsX: item.bounds.x,
		boundsY: item.bounds.y,
		boundsWidth: item.bounds.width,
		boundsHeight: item.bounds.height,
		layers: [],
		templateFile: null
	};
};

export const resizeArea = (area: ModelArea, targetWidth: number, targetHeight: number, anchor?: 'top' | 'bottom') => {
    const paperScope = new paper.PaperScope();
    paperScope.setup(new paper.Size(area.boundsWidth, area.boundsHeight));

    const path = paperScope.project.importJSON(area.path);
    path.scale(targetWidth / path.bounds.width, targetHeight / path.bounds.height);

    const delta = (path.bounds.height - area.boundsHeight) / 2;

    if (anchor) {
        if (anchor === 'top') path.position.y += delta;
        else if (anchor === 'bottom') path.position.y -= delta;
    }

    return {
        ...area,
        ...{
            pathSVG: path.exportSVG({ asString: true }) as string,
            path: path.exportJSON(),
            boundsX: path.bounds.x,
            boundsY: path.bounds.y,
            boundsWidth: path.bounds.width,
            boundsHeight: path.bounds.height
        }
    };
};

export const resizeAreaInCM = (area: ModelArea, ppcm: number, targetWidth: number, targetHeight: number, anchor?: 'top' | 'bottom') => {
    const targetWidthPx = targetWidth * ppcm;
    const targetHeightPx = targetHeight * ppcm;

    return resizeArea(area, targetWidthPx, targetHeightPx, anchor);
};

const getAllChildrens = (item: paper.Item) => {
    let items: paper.Item[] = [];

    const getChilds = (parent: paper.Item) => {
        items = [...items, parent];

        if (parent.children) {
            for (const child of parent.children) {
                getChilds(child);
            }
        }
    };

    getChilds(item);

    return items;
};
